import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'

import { Amplify, Auth } from 'aws-amplify'
import { awsconfig, awsconfiglocal } from 'config'
import { AWSCognitoProvider as AuthProvider } from 'contexts/AuthContext'
import { ConfigProvider } from 'contexts/ConfigContext'
import { ToastProvider } from 'contexts/ToastContext'
import { OpenAPI } from 'generated'
import { localAuthHeaders, setSessionUserId } from 'utils/localAuth'
import App from './App'
import reportWebVitals from './reportWebVitals'

import 'simplebar/src/simplebar.css'
import 'assets/third-party/apex-chart.css'
import 'assets/third-party/react-table.css'

const container = document.getElementById('root')
const root = createRoot(container!)

const config = process.env.REACT_APP_ROOT_DOMAIN ? awsconfig : awsconfiglocal

Amplify.configure(config)

const refreshToken = async () => {
  try {
    const data = await Auth.currentSession()
    const accessToken = data.getAccessToken()
    const jwt = accessToken.getJwtToken()
    OpenAPI.TOKEN = jwt
    // The local backend has no Envoy in front of it to turn this token into
    // identity headers, so remember who we are and send them ourselves.
    // Inert unless REACT_APP_API_BASE is set - see utils/localAuth.
    setSessionUserId(accessToken.payload?.sub)
  } catch (err: any) {
    console.log(err)
  }
}

// Resolved per request, so the identity is picked up as soon as the session
// exists rather than being frozen at start-up.
OpenAPI.HEADERS = async () => localAuthHeaders()

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: failureCount => {
        if (failureCount === 1) {
          refreshToken()
        }

        if (failureCount < 2) {
          return true
        }

        return false
      },
      retryDelay: retryAttempt => Math.min(5000 * 2 ** retryAttempt, 10000),
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: failureCount => {
        if (failureCount === 1) {
          refreshToken()
        }

        if (failureCount < 2) {
          return true
        }

        return false
      },
      retryDelay: retryAttempt => Math.min(5000 * 2 ** retryAttempt, 10000),
    },
  },
  queryCache: new QueryCache({
    onError: refreshToken,
  }),
  mutationCache: new MutationCache({
    onError: refreshToken,
  }),
})

root.render(
  <QueryClientProvider client={queryClient}>
    <ConfigProvider>
      <BrowserRouter>
        <ToastProvider>
          <AuthProvider>
            <App />
          </AuthProvider>
        </ToastProvider>
      </BrowserRouter>
    </ConfigProvider>
  </QueryClientProvider>
)

reportWebVitals()
